import { useState } from 'react';
import {
  DatePicker,
  Input,
  Select,
  Button,
  Table,
  Card,
  Row,
  Col,
  Space,
  Tag,
  Flex,
  Typography
} from 'antd';
import { SearchOutlined, FilterOutlined, ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { styled } from '@mui/system';
import { useSelector } from 'react-redux';
import AuthService from '../../../Components/auth/AuthService';
import { useLoadingDispatch, showLoading, hideLoading } from '../../../context/loadingContext';
import { showMessage } from '../../../Components/Utils/message';
import { useNavigate } from 'react-router-dom';

const Auth = new AuthService();

const DataSheet = styled('div')`
   .ant-card-body {
      padding: 10px;
    };
    .ant-card-head {
      min-height: 40px;
    };
`;

const { RangePicker } = DatePicker;
const { Option } = Select;

const BatchReport = () => {
  const { products, locations, roles } = useSelector((state) => state.AppState?.userCreds);
  const [activeFilter, setActiveFilter] = useState(null);
  const layoutDispatch = useLoadingDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: null,
    product: null,
    location: null,
    batchCode: '',
  });

  const formatExpiryDate = (expiry) => {
    if (!expiry || expiry === "0000-00-00") {
      // return original value if it's empty or invalid
      return expiry;
    }

    const expiryDate = dayjs(expiry, "YYYY-MM-DD", true);
    if (!expiryDate.isValid()) {
      // return original if parsing fails
      return expiry;
    }

    return expiryDate.format("DD-MM-YYYY"); // or any format you prefer
  };

  const columns = [

    {
      title: 'Product',
      render: (record) => {
        const selected = products?.find((item) => item.netsuite_id == record?.product_netsuite_id)
        return (
          <>
            {selected?.code ? `${selected?.code} - ${selected?.name}` : ""}
          </>
        );
      },
      // width: 60
    },
    {
      title: 'Location',
      render: (record) => {
        const selected = locations?.find((item) => item.id == record?.location_id)
        return (
          <>
            {selected?.code}
          </>
        );
      },
      // width: 60
    },
    {
      title: "Quantity",
      render: (record) => {
        const quantity = Number(record?.stock_level) || 0;
        const expiry = record?.expiry_date;

        let color = "green"; // default safe
        if (expiry) {
          const expiryDate = dayjs(expiry, "YYYY-MM-DD");
          const today = dayjs();
          const diffDays = expiryDate.diff(today, "day");

          if (diffDays < 0) {
            color = "red"; // expired
          } else if (diffDays <= 7) {
            color = "orange"; // expiring soon
          }
        }

        return (
          <Tag color={color} style={{ fontWeight: "bold", fontSize: 16 }}>
            {quantity}
          </Tag>
        );
      },
      align: "center",
    },
    {
      title: "Batch Code",
      render: (record) => {
        return (
          <>
            {record?.batch_code ? record?.batch_code : ""}
          </>
        )
      },
      // width: 100
    },
    {
      title: "Supplier Batch Code",
      render: (record) => {
        return (
          <>
            {record?.supplier_batch_code ? record?.supplier_batch_code : ""}
          </>
        )
      },
      // width: 120
    },
    {
      title: "Expiry",
      render: (record) => {
        const expiry = record?.expiry_date;
        if (!expiry) return "";

        const expiryDate = dayjs(expiry, "YYYY-MM-DD");
        const today = dayjs();
        const diffDays = expiryDate.diff(today, "day");

        let color = "green";
        if (diffDays < 0) {
          color = "red";
        } else if (diffDays <= 7) {
          color = "orange";
        }

        return <Tag style={{ fontSize: '15px' }} color={color}>{record?.expiry_date}</Tag>;
      }
    },

  ];

  const handleSearch = async (extraCondition = null) => {
    console.log('Filters:', filters);
    const [start, end] = filters.dateRange || [];

    if (!filters.product && !filters.location && !filters.batchCode && (!start || !end) && !extraCondition) {
      showMessage("error", "Please select at least one filter (Date Range, product, location, or Batch Code)");
      return;
    }

    if ((start && !end) || (!start && end)) {
      showMessage("error", "Please select a complete date range");
      return;
    }

    const payload = {};
    if (filters.product) payload.product_netsuite_id = filters.product;
    if (filters.location) payload.location_id = filters.location;
    if (filters.batchCode) payload.batch_code = filters.batchCode;
    if (start && end) {
      payload.start_date = dayjs(start).format("YYYY-MM-DD");
      payload.end_date = dayjs(end).format("YYYY-MM-DD");
    }

    if (extraCondition === 'expired') {
      payload.expiry_status = 1;
    } else if (extraCondition === 'soon') {
      payload.expiry_status = 2;
    }

    showLoading(layoutDispatch);
    try {
      const res = await Auth.fetch(`/report/stock-expiry`, {
        method: "POST",
        body: JSON.stringify(payload),
      })
      if (res?.ack) {
        setData(res?.stock_expiry);
        if (res?.stock_expiry?.length > 0) {
        } else {
          setData([]);
          showMessage('error', 'No data found')
        }
      } else {
        setData([]);
        showMessage('error', res?.message)
      }

    } catch (err) {
      console.log(err);
      showMessage('error', 'Something went wrong');

    } finally {
      hideLoading(layoutDispatch)
    }

    console.log("Payload:", payload);
  };

  const handleQuickFilter = (condition) => {
    setActiveFilter(condition);
    setFilters({
      dateRange: null,
      product: null,
      location: null,
      batchCode: '',
    });
    handleSearch(condition);
  };

  const cardFilters = [
    {
      key: "expired",
      label: "Expired",
      icon: <ExclamationCircleOutlined />,
      color: "#ff4d4f",
      bg: "#fff1f0",
    },
    {
      key: "soon",
      label: "Expiring Soon",
      icon: <ClockCircleOutlined />,
      color: "#fa8c16",
      bg: "#fff7e6",
    },
  ];

  return (
    <DataSheet>



      <Card title={<><FilterOutlined style={{ color: '#6cb8ff' }} /> Batch Expiry</>} style={{ marginBottom: '10px' }}>
        <Row gutter={[6, 1]}>
          <Col xs={24} md={12} lg={6}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>Expiry Range</label>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={(dates) => {
                setFilters(prev => ({ ...prev, dateRange: dates }))
                setActiveFilter(null);
              }
              }
              format="DD-MM-YYYY"
            />
          </Col>
          <Col xs={24} md={12} lg={5}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>Product</label>
            <Select
              style={{ width: '100%' }}
              placeholder="Product"
              allowClear
              showSearch
              value={filters.product}
              onChange={(value) => {
                setFilters(prev => ({ ...prev, product: value }))
                setActiveFilter(null);
              
              }}
              optionFilterProp="children"
            >
              {products.map(product => (
                <Option key={product?.netsuite_id} value={product?.netsuite_id}>{`${product?.code} - ${product?.name}`}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={12} lg={4}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>Location</label>
            <Select
              style={{ width: '100%' }}
              placeholder="Location"
              allowClear
              showSearch
              value={filters.location}
              onChange={(value) => {
                setFilters(prev => ({ ...prev, location: value }))
                setActiveFilter(null);
              } }
              optionFilterProp="children"
            >
              {locations.map(location => (
                <Option key={location?.id} value={location?.id}>{`${location?.code}`}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={12} lg={6}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>Batch Code</label>
            <Input
              placeholder="Batch code"
              value={filters.batchCode}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, batchCode: e.target.value }))
                setActiveFilter(null);
              }}
            />
          </Col>
          <Col xs={24} md={12} lg={2}>
            <Space style={{ width: '100%', marginTop: '25px' }}>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
              >
                Search
              </Button>
            </Space>
          </Col>

        </Row>
        <Flex align="center" gap={6} style={{ marginTop: 16, marginBottom: 4 }}>
        <FilterOutlined style={{ color: '#1890ff' }} />
        <span style={{ fontWeight: 600, color: '#1890ff' }}>Quick Filters</span>
      </Flex>
        <Flex gap={12} wrap align='center' style={{ marginTop: 5 }}>
          {cardFilters.map((item) => {
            const isActive = activeFilter === item.key;
            return (
              <Card
                key={item.key ?? "reset"}
                hoverable
                onClick={() => handleQuickFilter(item.key)}
                style={{
                  cursor: "pointer",
                  width: 130,
                  height: 36,
                  textAlign: "center",
                  borderRadius: 10,
                  background: isActive ? item.color : item.bg,
                  color: isActive ? "white" : item.color,
                  border: isActive
                    ? `1px solid ${item.color}`
                    : `1px solid #d9d9d9`,
                  boxShadow: isActive
                    ? `0 0 8px ${item.color}55`
                    : "0 1px 3px rgba(0,0,0,0.1)",
                  transition: "all 0.25s ease",
                  display: "flex",
                  // flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  // gap: 6,
                }}
              >
                <Flex align="center" gap={6}>
                  {item.icon}
                  {item.label}
                </Flex>

              </Card>
            );
          })}
        </Flex>
      </Card>

      {data.length > 0 && (
        <Card
          title={
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              Search Results ({data.length} items)
            </span>
          }
        // extra={
        //   <Button icon={<DownloadOutlined />} type="default" onClick={handleExportCSV}>

        //     Export
        //   </Button>
        // }
        >
          <Table
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={false}
            // bordered
            scroll={{ x: 'max-content' }}
            rowClassName={(_, index) => (index % 2 === 1 ? "table-row-striped" : "")}
          />
        </Card>
      )}
    </DataSheet>
  );
};

export default BatchReport;